import Phaser from 'phaser';
import type { IsoProjection } from '../core/iso';
import type { LightId } from '../world/biome';
import { LIGHT_GRADE } from './biomes';
import { LAYER } from './depth';

const GLOW_KEY = 'light-glow';

export interface LightSource {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /** Radius in tiles. */
  readonly radius: number;
  readonly color: number;
}

/**
 * Time of day (D132). Dusk and night lay a multiply grade over the world
 * (below selection outlines and the HUD), and every light source — your
 * buildings, burning nests — adds a soft additive glow on top, so a colony at
 * night is a pool of warm light in the dark. Visual only.
 */
export class Lighting {
  private readonly shade: Phaser.GameObjects.Rectangle | null = null;
  private readonly glows = new Map<number, Phaser.GameObjects.Image>();
  private readonly strength: number;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly iso: IsoProjection,
    light: LightId,
  ) {
    const grade = LIGHT_GRADE[light];
    this.strength = grade.lights;
    if (light === 'day') return;
    if (!scene.textures.exists(GLOW_KEY)) {
      const size = 256;
      const tex = scene.textures.createCanvas(GLOW_KEY, size, size);
      if (tex) {
        const g = tex.getContext();
        const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
        grad.addColorStop(0, 'rgba(255,255,255,0.9)');
        grad.addColorStop(0.35, 'rgba(255,255,255,0.45)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = grad;
        g.fillRect(0, 0, size, size);
        tex.refresh();
      }
    }
    // A screen-sized, camera-fixed sheet (oversized so zooming out never shows its edge).
    this.shade = scene.add
      .rectangle(0, 0, 10, 10, grade.tint, 1)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setBlendMode(Phaser.BlendModes.MULTIPLY)
      .setDepth(LAYER.overlay - 10);
    this.resize();
  }

  get active(): boolean {
    return this.shade !== null;
  }

  get glowCount(): number {
    return this.glows.size;
  }

  resize(): void {
    if (!this.shade) return;
    const cam = this.scene.cameras.main;
    // Scroll-factor-0 objects still scale with zoom around the camera centre.
    const k = 2 / Math.max(0.2, cam.zoom);
    this.shade.setPosition(cam.width / 2, cam.height / 2).setSize(cam.width * k, cam.height * k);
  }

  /** Match the glows to the current light sources (cheap: only adds / removes what changed). */
  sync(sources: readonly LightSource[]): void {
    if (!this.shade) return;
    const seen = new Set<number>();
    for (const s of sources) {
      seen.add(s.id);
      let img = this.glows.get(s.id);
      if (!img) {
        img = this.scene.add.image(0, 0, GLOW_KEY).setBlendMode(Phaser.BlendModes.ADD).setDepth(LAYER.overlay - 9);
        this.glows.set(s.id, img);
      }
      const c = this.iso.gridToWorld(s.x, s.y);
      const w = s.radius * this.iso.tileWidth * 2;
      img.setPosition(c.x, c.y).setDisplaySize(w, w / 2).setTint(s.color).setAlpha(0.55 * this.strength);
    }
    for (const [id, img] of this.glows) {
      if (seen.has(id)) continue;
      img.destroy();
      this.glows.delete(id);
    }
  }

  destroy(): void {
    this.shade?.destroy();
    for (const g of this.glows.values()) g.destroy();
    this.glows.clear();
  }
}
